import React, { PureComponent } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'
import cn from 'classnames'

import FullscreenHandler from '../FullscreenHandler'
import { PROP_TYPE_CHILDREN } from '../constants'

const getOrCreateToaster = function (parentNode: Element) {
  let toaster
  const existingToaster = document.querySelectorAll('.mc-toaster')
  if (existingToaster.length) {
    toaster = existingToaster.item(0)
    const existingParent = toaster.parentNode
    if (existingParent !== parentNode) {
      existingParent?.removeChild(toaster)
      parentNode?.appendChild(toaster)
    }
  } else {
    toaster = document.createElement('div')
    toaster.className = 'mc-toaster'
    parentNode?.appendChild(toaster)
  }

  return toaster
}

const TOAST_TRANSITION = {
  type: 'spring',
  mass: 1,
  damping: 30,
  stiffness: 200,
  duration: 1,
} as const

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  kind?: 'default' | 'success' | 'error'
  show?: boolean
}

interface State {
  show: boolean
}

export default class Toast extends PureComponent<ToastProps, State> {
  static propTypes = {
    children: PROP_TYPE_CHILDREN,
    className: PropTypes.string,
    kind: PropTypes.oneOf(['default', 'success', 'error']),
    show: PropTypes.bool,
  }

  static defaultProps = {
    kind: 'default',
  }

  state = {
    show: false,
  }

  containerRef = React.createRef<HTMLDivElement>()

  timer: number | undefined = undefined

  show = () => {
    this.clearTimer()
    this.setState({
      show: true,
    })
    this.timer = window.setTimeout(this.hide, 6000)
  }

  hide = () => {
    this.clearTimer()
    this.setState({ show: false })
  }

  componentDidMount() {
    if (this.props.show) {
      this.show()
    }
  }

  componentDidUpdate(prevProps: ToastProps) {
    if (this.props.show !== prevProps.show) {
      if (this.props.show) {
        this.show()
      } else {
        this.hide()
      }
    }
  }

  componentWillUnmount() {
    this.clearTimer()
  }

  clearTimer = () => {
    window.clearTimeout(this.timer)
  }

  resumeHide = () => {
    if (this.state.show) {
      this.show()
    }
  }

  renderToast() {
    const { children, className, kind } = this.props

    const { show } = this.state

    const classNames = cn({
      'mc-toast': true,
      [`mc-toast--${kind}`]: kind,
      [className ?? '']: className,
    })

    return (
      <AnimatePresence mode='sync' initial={false}>
        {show && (
          <motion.div
            key='toast'
            layout
            className={classNames}
            onMouseOver={this.clearTimer}
            onFocus={this.clearTimer}
            onBlur={this.resumeHide}
            onMouseOut={this.resumeHide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: -30,
              transition: TOAST_TRANSITION,
            }}
            transition={TOAST_TRANSITION}
            style={{
              transformOrigin: 'center bottom',
              zIndex: 2,
            }}
          >
            <div className='mc-toast__container' ref={this.containerRef}>
              <div className='mc-toast__content'>{children}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  render() {
    return (
      <FullscreenHandler>
        {({ fullscreenElement }) =>
          createPortal(
            this.renderToast(),
            getOrCreateToaster(fullscreenElement || document.body),
          )
        }
      </FullscreenHandler>
    )
  }
}
